import styled from '@emotion/styled';
import { H1SEO, H2, H3, SmallText, Text, TextLabel } from 'ds/components/typography';
import Icon from 'assets/svg/price-us.svg';
import { Link } from 'ds/components/link';
import { typographyFontSizeCfg } from 'ds/components/typography/types';

export const OfferDetailsLabel = styled(TextLabel)<{ isNew?: boolean }>`
  ${({ theme, isNew }) => `
    color: ${theme.colors.neutrals.grey1};
    background-color: ${isNew ? theme.colors.label.yellow : theme.colors.brand.mocha};
    border: 1px solid ${isNew ? theme.colors.label.yellow : theme.colors.neutrals.grey8};
    border-radius: ${theme.borderRadius.tiny};
    padding: ${theme.spacing(0.5)} ${theme.spacing(0.75)};
    font-weight: 700;
    font-size: 11px;
  `}
`;

export const Divider = styled(Text)`
  margin: 0 8px;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
`;

export const DeveloperLink = styled(Link)`
  color: ${({ theme }) => theme.colors.brand.main};
  font-weight: 400;
  font-size: 15px;
  text-decoration: underline;
`;

export const DeveloperNameWrapper = styled(Text.withComponent('a'))`
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
  flex-shrink: 1;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.brand.main};
  text-decoration: underline;
`;

export const DeveloperWrapper = styled.div<{ multiple?: boolean }>`
  ${({ theme }) => `
    margin-bottom: -${theme.spacing(1)};
    display: flex;
    align-items: center;
    & > div:first-of-type {
      margin-left: ${theme.spacing(0.5)};
    }
  `}
  ${({ theme, multiple }) => theme.media.showAt({ at: 1 }) `
    padding: ${multiple ? 0 : theme.spacing(1)} 0 ${multiple ? theme.spacing(0.5) : ''};
    margin: 0;
    overflow: hidden;
    > * {
      flex-shrink: 0;
    }
  `}
`;

export const DeveloperInfoWrapper = styled.div<{ multiple?: boolean }>`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme, multiple }) => theme.spacing(multiple ? 1.5 : 7)};
`;

export const DeveloperInfoInnerWrapper = styled.div<{ multiple?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-right: ${({ theme }) => theme.spacing(1)};
  ${({ multiple }) => multiple ? `width: 70vw;` : ''}
  * {
    font-size: 13px !important;
  }
`;

export const ProjectTitle = styled(H2)<{ isLeft?: boolean }>`
  font-size: 29px !important;
  font-weight: 700 !important;
  line-height: 36px !important;
  text-align: ${({ isLeft }) => isLeft ? 'left' : 'right'};
`;

export const ProjectTitleSEO = styled(H1SEO)`
  font-size: 29px !important;
  font-weight: 700 !important;
  line-height: 36px !important;
`;

export const ProjectSubtitle = styled(H3)<{ isLeft?: boolean }>`
  font-size: 24px !important;
  font-weight: 400 !important;
  line-height: 36px !important;
  text-align: ${({ isLeft }) => isLeft ? 'left' : 'right'};
`;

export const MainInfoBlock = styled.div<{ isModeB?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${({ theme, isModeB }) => theme.media.showAt({ at: 1 }) `
    flex-direction: column;
    align-items: flex-start;
    & > div:last-of-type {
      margin-top: ${theme.spacing(isModeB ? 1 : 3)};
    }
    & h2 {
      font-weight: 700;
    }
  `}
`;

export const RoomsLabelWrapper = styled.div<{ isCommercial?: boolean }>`
  > * { text-align: left }
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    > * { text-align: right }
  `}
  ${({ theme, isCommercial }) => isCommercial ? `
    color: ${theme.colors.neutrals.grey3};
  ` : ''}
`;

export const PriceLabelWrapper = styled(RoomsLabelWrapper)`
  > * { text-align: right }
  color: ${({ theme }) => theme.colors.neutrals.grey3};
`;

export const MainInfoItem = styled.div<{ isCommercial?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ theme, isCommercial }) => isCommercial ? `
    color: ${theme.colors.neutrals.grey1};
  ` : ''}
`;

export const LabelsRow = styled.div<{ isModeB?: boolean }>`
  :empty {
    display: none;
  }
  ${({ theme, isModeB }) => `
    margin-top: ${isModeB ? theme.spacing(1) : theme.spacing(3)};
    ${isModeB ? `margin-bottom: ${theme.spacing(4)};` : ''}
    display: flex;
    align-items: center;
    > * {
      margin-left: ${theme.spacing(1)};
    }
  `}
  ${({ theme, isModeB }) => theme.media.showAt({ at: 1 }) `
    ${isModeB ? `margin: ${theme.spacing(2)} 0;` : ''}
    flex-wrap: wrap;
    > *:nth-of-type(3) {
      flex-basis: 100%;
      margin-top: ${theme.spacing(1)};
      > div {
        display: inline-block;
      }
    }
  `}
`;

export const AdditionalInfoRow = styled.div<{ isCommercial?: boolean; isModeB?: boolean }>`
  ${({ theme }) => `
    margin-top: ${theme.spacing(2)};
  `}
  ${({ theme, isModeB }) => theme.media.showAt({ at: 1 }) `
    ${isModeB ? 'margin-top: 0;' : ''}
    > div {
      > div svg { margin-left: ${theme.spacing(0.5)} }
      > div:after { margin: 0 ${theme.spacing(1)} }
    }
  `}
`;

export const DetailsHeader = styled(H3)<{ modeB?: boolean}>`
  ${({ theme, modeB }) => `
    margin-bottom: ${theme.spacing(modeB ? 0 : 2)};
    padding: ${modeB ? `${theme.spacing(3)} ${theme.spacing(2)} 0` : 0};
  `}
`;

export const DescriptionBody = styled.div`
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey1};
    white-space: pre-line;
    padding-bottom: ${theme.spacing(1)};

    h1, h2, h3, h4, h5 {
      padding: 0 0 ${theme.spacing(1)};
      display: inline-block;
      width: 100%;
    }

    p, div {
      padding: 0 0 ${theme.spacing(1)};
    }

    center {
      padding: 0 0 ${theme.spacing(1)};
      display: block;
      text-align: center;
    }

    a {
      color: ${theme.colors.brand.main};
    }

    ul,
    ol {
      padding: ${theme.spacing(1)} 14px ${theme.spacing(1)} 0;
      margin: 0 0 0 ${theme.spacing(2)};
      display: inline-block;
      white-space: normal;
      width: 100%;
      li {
        margin: ${theme.spacing(0.5)} 0;
      }
    }

    ul {
      li {
        list-style-type: circle;
      }
    }

    ol {
      li {
        list-style-type: decimal;
      }
    }

    u {
      text-decoration: underline;
    }

    b, strong {
      font-weight: 700;
    }
  `}
`;

export const DescriptionReadMore = styled.span`
  display: inline;
  padding-right: ${({ theme }) => theme.spacing(0.5)};
  cursor: pointer;
`;

export const MobileSmallText = styled(SmallText)`
  color: ${({ theme }) => theme.colors.neutrals.grey3};
`;

export const MainInfoItemMobile = styled.div<{ isModeB?: boolean }>`
  border-radius: ${({ theme }) => theme.borderRadius.tiny};
  background-color: ${({ isModeB }) => isModeB ? 'rgba(124, 84, 255, 0.12)' : '#F2F6F7'};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
  display: flex;
  ${({ isModeB }) => isModeB ? 'justify-self: flex-start;' : ''}
  color: ${({ theme, isModeB }) => isModeB ? theme.colors.insights.prices : theme.colors.brand.main};
  align-items: center;
  ${({ theme, isModeB }) => isModeB ? `
    margin: ${theme.spacing(2)} 0 ${theme.spacing(4)};
  ` : `
    &&& {
      margin-top: ${theme.spacing(1)};
    }
  `}
`;

export const PriceIconMobile = styled(Icon)`
  margin-left: ${({ theme }) => theme.spacing(1)};
  path {
    fill: ${({ theme }) => theme.colors.brand.main};
  }
`;

export const NotPromotedLabel = styled(TextLabel)`
  background-color: ${({ theme }) => theme.colors.brand.mocha};
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  border-radius: ${({ theme }) => theme.borderRadius.tiny};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(0.75)}`};
  font-size: ${typographyFontSizeCfg.textLabel.small}px;
  font-weight: 700;
  width: fit-content;
`;
